import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { API } from "@/App";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import PhoneInput from "@/components/PhoneInput";
import { 
  ArrowLeft, User, Phone, Mail, MapPin, Briefcase, 
  Award, Clock, Check, Building, Globe, FileText,
  Dog, Scissors, Heart, Shield, Home, GraduationCap
} from "lucide-react";

const professionTypes = [
  { id: "dog_sitter", name: "Dog Sitter", icon: Dog, description: "Accudisco cani a domicilio o presso di me" },
  { id: "cinofilo", name: "Educatore Cinofilo", icon: GraduationCap, description: "Addestramento e correzione comportamentale" },
  { id: "pensione", name: "Pensione/Asilo", icon: Home, description: "Struttura per ospitare animali" },
  { id: "toelettatura", name: "Toelettatura", icon: Scissors, description: "Servizi di grooming e igiene" },
  { id: "veterinario", name: "Veterinario", icon: Heart, description: "Clinica o ambulatorio veterinario" },
  { id: "assicurazione", name: "Assicurazioni Pet", icon: Shield, description: "Polizze per animali domestici" },
  { id: "negozio", name: "Negozio Animali", icon: Building, description: "Pet shop e forniture" },
];

export default function ProfessionalRegister() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    profession_type: "",
    profession_types: [], // Multiple profession types
    description: "",
    city: "",
    zone: "",
    address: "",
    phone: "",
    email: "",
    website: "",
    services: [],
    price_from: "",
    experience_years: "",
    certifications: []
  });

  const [newService, setNewService] = useState("");
  const [newCert, setNewCert] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleProfessionType = (typeId) => {
    setFormData(prev => {
      const types = prev.profession_types.includes(typeId)
        ? prev.profession_types.filter(t => t !== typeId)
        : [...prev.profession_types, typeId];
      return {
        ...prev,
        profession_types: types,
        profession_type: types[0] || "" // Primary type is the first selected
      };
    });
  };

  const addService = () => {
    if (newService.trim()) {
      setFormData(prev => ({ ...prev, services: [...prev.services, newService.trim()] }));
      setNewService("");
    }
  };

  const removeService = (index) => {
    setFormData(prev => ({ ...prev, services: prev.services.filter((_, i) => i !== index) }));
  };

  const addCertification = () => {
    if (newCert.trim()) {
      setFormData(prev => ({ ...prev, certifications: [...prev.certifications, newCert.trim()] }));
      setNewCert("");
    }
  };

  const removeCertification = (index) => {
    setFormData(prev => ({ ...prev, certifications: prev.certifications.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.profession_types.length || !formData.phone || !formData.email || !formData.city) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/professionals/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          profession_type: formData.profession_types[0], // Primary type
          price_from: formData.price_from ? parseFloat(formData.price_from) : null,
          experience_years: formData.experience_years ? parseInt(formData.experience_years) : null
        })
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success("Registrazione inviata con successo!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nella registrazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-sm">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <Check className="text-emerald-600" size={40} />
          </div>
          <h1 className="text-2xl font-bold text-[#1D1D1F] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Richiesta Inviata!
          </h1>
          <p className="text-[#86868B] mb-6">
            Grazie per la tua registrazione! Il team Zampa verificherà i tuoi dati e ti contatterà via email entro 48 ore.
          </p>
          <p className="text-sm text-[#86868B] mb-6">
            Una volta approvato, il tuo profilo apparirà nell'app con il badge <span className="text-[#FF6B4A] font-semibold">✓ Certificato Zampa</span>
          </p>
          <Button onClick={() => navigate("/")} className="w-full bg-[#1D1D1F] hover:bg-[#2D2D2F] rounded-xl h-12">
            Torna alla Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-xl bg-[#F5F5F7] flex items-center justify-center">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#1D1D1F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Registrati come Professionista
              </h1>
              <p className="text-sm text-[#86868B]">Entra nella rete Zampa</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Already registered? */}
        <div className="bg-blue-50 rounded-xl p-4 mb-6 flex items-center justify-between">
          <p className="text-sm text-blue-800">Hai già un account approvato?</p>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/professional/portal')}
            className="rounded-xl border-blue-300 text-blue-700 hover:bg-blue-100"
          >
            Accedi al Portale
          </Button>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${
                step >= s ? 'bg-[#FF6B4A] text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {s}
              </div>
              {s < 3 && <div className={`w-12 h-1 rounded ${step > s ? 'bg-[#FF6B4A]' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
              <h2 className="text-lg font-bold text-[#1D1D1F]">Informazioni Base</h2>
              
              {/* Profession Type Selection - Multiple */}
              <div>
                <label className="block text-sm font-medium text-[#1D1D1F] mb-2">
                  Tipo di Attività * <span className="font-normal text-[#86868B]">(puoi selezionarne più di uno)</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {professionTypes.map(type => {
                    const Icon = type.icon;
                    const isSelected = formData.profession_types.includes(type.id);
                    return (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => toggleProfessionType(type.id)}
                        className={`p-4 rounded-xl border-2 text-left transition-all relative ${
                          isSelected 
                            ? 'border-[#FF6B4A] bg-orange-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-[#FF6B4A] rounded-full flex items-center justify-center">
                            <Check className="text-white" size={14} />
                          </div>
                        )}
                        <Icon className={isSelected ? 'text-[#FF6B4A]' : 'text-gray-400'} size={24} />
                        <p className={`font-semibold mt-2 ${isSelected ? 'text-[#FF6B4A]' : 'text-[#1D1D1F]'}`}>
                          {type.name}
                        </p>
                        <p className="text-xs text-[#86868B] mt-1">{type.description}</p>
                      </button>
                    );
                  })}
                </div>
                {formData.profession_types.length > 1 && (
                  <p className="text-sm text-[#FF6B4A] mt-2 flex items-center gap-1">
                    <Check size={14} />
                    {formData.profession_types.length} attività selezionate
                  </p>
                )}
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Nome Attività / Professionista *</label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Es: Mario Rossi - Dog Sitter"
                  className="h-12 rounded-xl"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Descrizione *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Descrivi la tua attività, esperienza e servizi offerti..."
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#FF6B4A] focus:ring-1 focus:ring-[#FF6B4A] outline-none"
                />
              </div>

              <Button 
                type="button"
                onClick={() => setStep(2)}
                disabled={!formData.profession_types.length || !formData.name || !formData.description}
                className="w-full bg-[#FF6B4A] hover:bg-[#E05A3A] rounded-xl h-12"
              >
                Continua
              </Button>
            </div>
          )}

          {/* Step 2: Contact Info */}
          {step === 2 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
              <h2 className="text-lg font-bold text-[#1D1D1F]">Contatti e Località</h2>

              <div>
                <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Indirizzo completo *</label>
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(address, placeData) => {
                    setFormData(prev => ({
                      ...prev,
                      address: address,
                      city: placeData?.city || prev.city,
                      zone: placeData?.province || prev.zone
                    }));
                  }}
                  placeholder="Cerca indirizzo della tua attività..."
                />
                {formData.city && (
                  <p className="text-xs text-[#86868B] mt-1">
                    📍 Città: {formData.city} {formData.zone && `(${formData.zone})`}
                  </p>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Telefono *</label>
                  <PhoneInput
                    value={formData.phone}
                    onChange={(fullNumber) => setFormData(prev => ({ ...prev, phone: fullNumber }))}
                    placeholder="333 1234567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="info@tuaattivita.it"
                      className="h-12 rounded-xl pl-10"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Sito Web (opzionale)</label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://www.tuosito.it"
                    className="h-12 rounded-xl pl-10"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1 rounded-xl h-12">
                  Indietro
                </Button>
                <Button 
                  type="button"
                  onClick={() => setStep(3)}
                  disabled={!formData.city || !formData.phone || !formData.email}
                  className="flex-1 bg-[#FF6B4A] hover:bg-[#E05A3A] rounded-xl h-12"
                >
                  Continua
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Services & Experience */}
          {step === 3 && (
            <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
              <h2 className="text-lg font-bold text-[#1D1D1F]">Servizi e Esperienza</h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Prezzo minimo (€)</label>
                  <Input
                    name="price_from"
                    type="number"
                    value={formData.price_from}
                    onChange={handleChange}
                    placeholder="15"
                    className="h-12 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Anni esperienza</label>
                  <Input
                    name="experience_years"
                    type="number"
                    value={formData.experience_years}
                    onChange={handleChange}
                    placeholder="5"
                    className="h-12 rounded-xl"
                  />
                </div>
              </div>

              {/* Services */}
              <div>
                <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Servizi Offerti</label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newService}
                    onChange={(e) => setNewService(e.target.value)}
                    placeholder="Es: Passeggiate, Dog sitting notturno..."
                    className="h-10 rounded-xl"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
                  />
                  <Button type="button" onClick={addService} className="bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-xl">
                    Aggiungi
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.services.map((service, i) => (
                    <span key={i} className="px-3 py-1 bg-orange-50 text-[#FF6B4A] rounded-full text-sm flex items-center gap-2">
                      {service}
                      <button type="button" onClick={() => removeService(i)} className="hover:text-red-500">×</button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Certifications */}
              <div>
                <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Certificazioni</label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newCert}
                    onChange={(e) => setNewCert(e.target.value)}
                    placeholder="Es: ENCI, APNEC, Laurea in Veterinaria..."
                    className="h-10 rounded-xl"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
                  />
                  <Button type="button" onClick={addCertification} className="bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-xl">
                    Aggiungi
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.certifications.map((cert, i) => (
                    <span key={i} className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm flex items-center gap-2">
                      <Award size={14} />
                      {cert}
                      <button type="button" onClick={() => removeCertification(i)} className="hover:text-red-500">×</button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Privacy Notice */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-[#86868B]">
                  Inviando questa richiesta accetti che i tuoi dati vengano verificati dal team Zampa. 
                  Una volta approvato, il tuo profilo sarà visibile agli utenti dell'app.
                </p>
              </div>

              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setStep(2)} className="flex-1 rounded-xl h-12">
                  Indietro
                </Button>
                <Button 
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-[#FF6B4A] hover:bg-[#E05A3A] rounded-xl h-12"
                >
                  {loading ? "Invio in corso..." : "Invia Richiesta"}
                </Button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
